import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import Images from "assets/images"
import { CcLibrarySet, MtCartCreate } from "server/models"
import { PAYMENT_CURRENCY } from "const"
import {
    StoreType,
    ImportCcLibrarySet,
    CreateCartData,
    setCcLibrarySetImportId,
    setCcLibrarySetReloadImported,
    setBillingReloadCart,
} from "client/store"
import {
    Logo,
    Button,
    SpinnerIcon,
    TableColumn,
    BalloonMessage,
} from "client/components"

export type LibraryToAccountSet = { [librarySetId: number]: number } // { librarySetId => accountSetId }

const Columns = (
    importedFromLibrary: LibraryToAccountSet,
    importedFromAccount: number[],
    importFrom: "library" | "account",
    maxSetNumber: number,
) => {
    const cart = useSelector((state: StoreType) => state.billing.cart)
    const importSetId = useSelector((state: StoreType) => state.ccLibrarySet.importSetId)
    const project = useSelector((state: StoreType) => state.project.projectView)
    const accountList = useSelector((state: StoreType) => state.account.accountList)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [addedToCart, setAddedToCart] = useState<number[]>([])

    useEffect(() => {
        const setIds: number[] = []
        cart.forEach((row) => {
            if (row.librarySetId) setIds.push(row.librarySetId)
        })
        setAddedToCart(setIds)
    }, [cart])

    const importFromLibrary = importFrom === "library"

    const columns: TableColumn[] = [
        {
            field: "id",
            header: lang.id,
            sortable: true,
        },
        {
            field: "logo",
            header: lang.logo,
            centerAlign: true,
            formatter: (set: CcLibrarySet) => <Logo table logo={set.logo || Images.complianceSetLogo} />,
        },
        {
            field: "publicId",
            header: lang.publicId,
            sortable: true,
            cellMinWidth: "150px",
            cellMaxWidth: "300px",
        },
        {
            field: "name",
            header: lang.name,
            sortable: true,
            cellMinWidth: "200px",
            cellMaxWidth: "400px",
        },
        {
            field: "owner",
            header: lang.owner,
            cellMinWidth: "150px",
            formatter: (set: CcLibrarySet) => {
                const account = accountList[set.ownerAccount]
                return account.name
            },
        },
        {
            field: "locked",
            header: lang.locked,
            sortable: true,
            cellWidth: "100px",
            cellMinWidth: "100px",
            formatter: (set: CcLibrarySet) => (set.locked ? lang.yes : lang.no),
        },
    ]

    if (importFromLibrary) {
        columns.push({
            field: "cost",
            header: lang.cost,
            sortable: true,
            cellWidth: "100px",
            cellMinWidth: "100px",
            formatter: (set: CcLibrarySet) => (
                set.cost === 0
                    ? lang.free
                    : <span style={{ textTransform: "uppercase" }}>{`${set.cost.toFixed(2)} ${PAYMENT_CURRENCY}`}</span>
            ),
        })
    }

    columns.push({
        field: "import",
        cellWidth: "100px",
        cellMinWidth: "100px",
        centerAlign: true,
        formatter: (set: CcLibrarySet) => {
            let allowedToImport = true
            let shouldAddToCart = set.cost !== 0

            if (!importFromLibrary && project.id) { // import from account to project
                if (importedFromAccount.includes(set.id)) allowedToImport = false
            }
            if (importFromLibrary) {
                if (!project.id) { // import from library to account
                    if (importedFromLibrary[set.id]) {
                        allowedToImport = false
                        shouldAddToCart = false
                    }
                } else { // import from library to project
                    const accountLevelSetId = importedFromLibrary[set.id]
                    if (accountLevelSetId) shouldAddToCart = false
                    if (importedFromAccount.includes(accountLevelSetId)) allowedToImport = false
                }

                if (maxSetNumber && (Object.keys(importedFromLibrary).length + addedToCart.length) >= maxSetNumber) allowedToImport = false
                if (addedToCart.includes(set.id)) allowedToImport = false
            }

            return (
                <Button
                    variant="success"
                    disabled={!allowedToImport}
                    onClick={() => {
                        if (importSetId !== null) return
                        dispatch(setCcLibrarySetImportId(set.id))

                        // all sets that not belong to Super Admin have cost = 0
                        if (!shouldAddToCart) { // import
                            let promise: Promise<any>
                            if (importFromLibrary && project.id) {
                                promise = ImportCcLibrarySet(userData.accountId, null, set).then((newSet) => (
                                    ImportCcLibrarySet(userData.accountId, project.id, newSet)
                                ))
                            } else {
                                promise = ImportCcLibrarySet(userData.accountId, project.id, set)
                            }

                            promise.then(() => {
                                dispatch(setCcLibrarySetImportId(null))

                                dispatch(setCcLibrarySetReloadImported(false))
                                setTimeout(() => {
                                    dispatch(setCcLibrarySetReloadImported(true))
                                }, 0)
                            })
                        } else { // add to cart
                            const cartData: MtCartCreate = {
                                accountId: userData.accountId,
                                userId: userData.userId,
                                packageIdFrom: null,
                                packageIdTo: null,
                                packageAction: null,
                                librarySetId: set.id,
                                projectId: project.id,
                            }
                            CreateCartData(cartData).then(() => {
                                dispatch(setCcLibrarySetImportId(null))
                                BalloonMessage(lang.librarySetAddedToCart.replace("{0}", set.name))

                                dispatch(setBillingReloadCart(false))
                                setTimeout(() => {
                                    dispatch(setBillingReloadCart(true))
                                }, 0)
                            })
                        }
                    }}
                >
                    {
                        importSetId === set.id
                            ? <SpinnerIcon />
                            : shouldAddToCart ? lang.buy : lang.add
                    }
                </Button>
            )
        },
    })

    return columns
}
export default Columns
